import { LitElement, html, TemplateResult, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import innerStyles from './outline-modal.inner.pcss?inline';
import globalStyles from './outline-modal.global.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { SlotHelper } from '../../controllers/slot-helper';

export type ModalVariants = 'modal' | 'popover';

export type ModalSizes = 'full' | '';

/**
 * OutlineModal component
 * A customizable modal dialog component built with LitElement.
 *
 * @element outline-modal
 *
 * @property {string} openButtonSelector - Selector for the button that opens the
 * modal
 * @property {'left' | 'right' | 'center'} closeButtonXPosition - Horizontal
 * position of the close button
 * @property {'top' | 'bottom'} closeButtonYPosition - Vertical position of the
 * close button
 * @property {ModalVariants} variant - The variant of the modal.
 * @property {ModalSizes} size - The size of the modal.
 * @property {string | null} hasError - Indicates if the modal has an error and
 * stores the error message
 * @property {string} trapFocus - Enables/disables focus trapping within the
 * modal. Uses string to default true.
 *
 * @state {boolean} isOpen - Tracks if the modal is open
 * @state {HTMLElement[]} focusableElements - Stores focusable elements within
 * the modal
 *
 * @slot default - The main content of the modal
 * @slot close-button - The close button for the modal
 * @slot close-button-adjacent-content - Content to be placed adjacent to the
 * close button
 *
 * Component Functions:
 * - connectedCallback(): Sets up initial event listeners and error checking.
 * - disconnectedCallback(): Removes event listeners.
 * - firstUpdated(): Initializes the close button and sets up error handling.
 * - updated(): Handles updates to properties and manages listeners.
 * - showModal(): Opens the modal and sets up focus trapping if enabled.
 * - closeModal(): Closes the modal and removes event listeners.
 * - handleBackdropClick(): Closes the modal when clicking outside.
 * - updateFocusableElements(): Updates the list of focusable elements in the modal.
 * - focusCloseButton(): Focuses the close button or first focusable element.
 * - handleFocusTrap(): Manages focus trapping within the modal.
 * - getCloseButton(): Retrieves the close button element from the slot.
 * - render(): Renders the modal component with conditional error message.
 */
@customElement('outline-modal')
export class OutlineModal extends LitElement {
  // Adopt styles for the component
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  // Initialize SlotHelper to manage slot content
  private slots = new SlotHelper(this);

  /*
   * Query the dialog element in the shadow DOM
   */
  @query('dialog')
  private dialog!: HTMLDialogElement;

  /*
   * Property for the selector of the button that opens the modal
   */
  @property({ type: String, reflect: true, attribute: 'open-button-selector' })
  openButtonSelector?: string;

  /*
   * Property for the horizontal position of the close button
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'close-button-x-position',
  })
  closeButtonXPosition: 'left' | 'right' | 'center' = 'right';

  /*
   * Property for the vertical position of the close button
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'close-button-y-position',
  })
  closeButtonYPosition: 'top' | 'bottom' = 'top';

  /*
   * Property to indicate if the modal has an error, and if so what that error may be.
   */
  @property({ type: String, reflect: true })
  hasError: string | null = null;

  /**
   * Property to enable/disable focus trapping within the modal
   */
  @property({ type: String, reflect: true, attribute: 'trap-focus' })
  trapFocus = 'true';

  /**
   * Property to set variant the modal
   */
  @property({ reflect: true })
  variant: ModalVariants = 'modal';

  /**
   * Property to set size the modal
   */
  @property({ reflect: false, attribute: 'size' })
  modalSize: ModalSizes = '';

  /*
   * State variable to track if the modal is open
   */
  @state()
  private isOpen = false;

  /*
   * State variable to store focusable elements within the modal
   */
  @state()
  private focusableElements: HTMLElement[] = [];

  /*
   * State variable to store the close button element
   */
  @state()
  private closeButton: HTMLElement | null = null;

  /*
   * State variable to store the open button element
   */
  @state()
  private openButton: HTMLElement | null = null;

  // Bind event handlers to the class instance
  private openButtonClickHandler = this.showModal.bind(this);
  private closeButtonClickHandler = this.closeModal.bind(this);
  private backdropClickHandler = this.handleBackdropClick.bind(this);
  private focusTrapHandler = this.handleFocusTrap.bind(this);

  connectedCallback() {
    super.connectedCallback();
    this.updateOpenButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeOpenButtonListener();
    this.removeCloseButtonListener();
    document.removeEventListener('keydown', this.focusTrapHandler);
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('openButtonSelector')) {
      this.updateOpenButton();
    }
    if (changedProperties.has('trapFocus')) {
      this.updateFocusTrapListener();
    }
    this.updateCloseButton();
  }

  /**
   * Update the open button and attach event listener
   * This method is responsible for finding the open button, attaching the
   * click event listener,and handling error cases when the open button is
   * not found.
   */
  private updateOpenButton() {
    // Remove any existing event listener from the previous open button
    this.removeOpenButtonListener();

    // Check if an open button selector is provided
    if (this.openButtonSelector) {
      // Attempt to find the new open button in the document using the provided selector
      const newOpenButton = document.querySelector(
        this.openButtonSelector
      ) as HTMLElement;

      // If a new open button is found
      if (newOpenButton) {
        // Update the openButton property with the new button
        this.openButton = newOpenButton;

        // Attach the click event listener to the new open button
        // This listener will trigger the showModal method when the button is clicked
        this.openButton.addEventListener('click', this.openButtonClickHandler);
      } else {
        // If no open button is found, log a warning and set an error state

        const warningText = `OutlineModal: No open button found with selector "${this.openButtonSelector}"`;

        console.warn(warningText, this);

        // Set the hasError flag to true, which can be used for error handling in the component
        this.hasError = warningText;

        // Set the openButton property to null since no valid open button was found
        this.openButton = null;
      }
    } else {
      // If no open button selector is provided, set the openButton property to null
      this.openButton = null;
    }
  }

  /**
   * Remove event listener from the open button
   */
  private removeOpenButtonListener() {
    if (this.openButton) {
      this.openButton.removeEventListener('click', this.openButtonClickHandler);
    }
  }

  /**
   * Update the close button and attach event listener
   * This method is responsible for finding the close button, attaching the
   * click event listener, and handling error cases when the close button
   * is not found.
   */
  private updateCloseButton() {
    // Remove any existing event listener from the previous close button
    this.removeCloseButtonListener();

    // Attempt to get the new close button from the slot
    const newCloseButton = this.getCloseButton();

    if (newCloseButton) {
      // If a new close button is found, update the closeButton property
      this.closeButton = newCloseButton;

      // Attach the click event listener to the new close button
      // This listener will trigger the closeModal method when the button is clicked
      this.closeButton.addEventListener('click', this.closeButtonClickHandler);
    } else {
      // If no close button is found, log a warning and set an error state

      const warningText = `OutlineModal: No close button found in the close-button slot`;

      console.warn(warningText, this);

      // Set the hasError flag to true, which can be used for error handling in the component
      this.hasError = warningText;

      // Set the closeButton property to null since no valid close button was found
      this.closeButton = null;
    }
  }

  /**
   * Remove event listener from the close button
   */
  private removeCloseButtonListener() {
    if (this.closeButton) {
      this.closeButton.removeEventListener(
        'click',
        this.closeButtonClickHandler
      );
    }
  }

  /**
   * Update focus trap listener based on trapFocus property and modal state
   * This method manages the keydown event listener for focus trapping
   */
  private updateFocusTrapListener() {
    if (this.isOpen && this.trapFocus.toLowerCase() === 'true') {
      document.addEventListener('keydown', this.focusTrapHandler);
    } else {
      // If the modal is closed, remove the event listener
      document.removeEventListener('keydown', this.focusTrapHandler);
    }
  }

  /**
   * Position the dialog centered below the open button
   */
  private positionPopover() {
    if (!this.openButton || !this.dialog) return;

    if (this.isOpen) {
      const openButtonRect = this.openButton.getBoundingClientRect();
      const dialogRect = this.dialog.getBoundingClientRect();
      let arrowPosX = '50%';

      // Calculate the top position to place the dialog below the open button
      const topPosition = openButtonRect.bottom + window.scrollY;
      // Calculate the left position to center the dialog below the open button
      let leftPosition =
        openButtonRect.left +
        window.scrollX +
        openButtonRect.width / 2 -
        dialogRect.width / 2;

      // Check if the dialog is fully visible and adjust if necessary
      const viewportWidth = window.innerWidth;

      // Adjust left position if dialog overflows the viewport
      if (leftPosition < 0) {
        arrowPosX = `${openButtonRect.left + openButtonRect.width / 2}px`;
        leftPosition = 0;
      } else if (leftPosition + dialogRect.width > viewportWidth) {
        leftPosition = viewportWidth - dialogRect.width;
      }

      // Set the dialog's position
      this.dialog.style.position = 'absolute';
      this.dialog.style.top = `${topPosition}px`;
      this.dialog.style.left = `${leftPosition}px`;
      this.dialog.style.setProperty(
        '--outline-modal--popover--arrow-pos-x',
        arrowPosX
      );
    }
  }

  /**
   * Observer to reposition the popover when the size of the observed element changes
   */
  private popoverResizeObserver = new ResizeObserver(() =>
    this.positionPopover()
  );

  /**
   * Modal open function
   * This method is responsible for opening the modal and setting up
   * necessary event listeners and states.
   */
  showModal() {
    // If we're too early, bail.
    if (!this.dialog) return;

    this.dialog.showModal();
    this.dialog.addEventListener('click', this.backdropClickHandler);
    this.isOpen = true;

    if (this.variant === 'popover') {
      this.popoverResizeObserver.observe(document.body);
      this.positionPopover();
    }
    this.updateFocusableElements();
    this.focusCloseButton();
    this.updateFocusTrapListener();
  }

  /**
   * Modal close function.
   * This method is responsible for closing the modal and cleaning up event
   * listeners and states.
   */
  closeModal() {
    this.dialog.close();
    this.dialog.removeEventListener('click', this.backdropClickHandler);
    this.isOpen = false;
    this.updateFocusTrapListener();
    this.popoverResizeObserver.disconnect();
  }

  /**
   * Close the modal on backdrop clicks. (clicks outside the modal content)
   */
  private handleBackdropClick(event: MouseEvent) {
    if (event.target === this.dialog) {
      this.closeModal();
    }
  }

  /**
   * Update the list of focusable elements within the modal
   * This method is responsible for identifying and storing all focusable elements
   * within the modal, excluding the close button.
   */
  private updateFocusableElements() {
    // Query all potentially focusable elements within the modal
    // This includes links with href, buttons, textareas, text inputs,
    // radio buttons, checkboxes, and select elements
    const allElements = Array.from(
      this.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
      )
    ) as HTMLElement[];

    // Filter out the close button from the list of focusable elements
    // This is done to ensure that the close button is handled separately
    // in the focus management logic
    this.focusableElements = allElements.filter(el => el !== this.closeButton);
  }

  /**
   * Focus the close button or the first focusable element
   * This method is responsible for setting the initial focus when the modal opens.
   * It prioritizes focusing the close button for easy dismissal, but falls back to
   * the first focusable element if the close button is not available.
   */
  private focusCloseButton() {
    // Check if the close button exists
    if (this.closeButton) {
      // If the close button exists, focus it
      this.closeButton.focus();
    } else if (this.focusableElements.length > 0) {
      // If there's no close button but there are other focusable elements,
      // focus the first one in the list
      this.focusableElements[0].focus();
    }
  }

  /**
   * Handle focus trapping within the modal
   * This method ensures that keyboard focus remains within the modal when
   * it's open and focus trapping is enabled. It prevents users from
   * tabbing out of the modal, enhancing accessibility and user experience.
   *
   * @param {KeyboardEvent} event - The keyboard event triggered by the user
   */
  private handleFocusTrap(event: KeyboardEvent) {
    if (
      event.key === 'Tab' &&
      this.isOpen &&
      this.trapFocus.toLowerCase() === 'true'
    ) {
      // Create an array of focusable elements, including the close button
      // Filter out any null or undefined elements
      const focusableElements = [
        this.closeButton,
        ...this.focusableElements,
      ].filter(Boolean) as HTMLElement[];

      // If there are no focusable elements, exit the function
      if (focusableElements.length === 0) return;

      // Get the first and last focusable elements
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Handle Shift+Tab (backwards tabbing)
      if (event.shiftKey) {
        // If focus is on the first element and user is tabbing backwards,
        // move focus to the last element
        if (document.activeElement === firstElement) {
          event.preventDefault(); // Prevent default tab behavior
          lastElement.focus(); // Move focus to the last element
        }
      } else {
        // Handle Tab (forward tabbing)
        // If focus is on the last element and user is tabbing forward,
        // move focus to the first element
        if (document.activeElement === lastElement) {
          event.preventDefault(); // Prevent default tab behavior
          firstElement.focus(); // Move focus to the first element
        }
      }
    }
  }

  /**
   * Get the close button element from the slot
   * @returns The close button element or null if not found
   */
  private getCloseButton(): HTMLElement | null {
    // If we don't have something in the close button slot, bail.
    if (!this.slots.isNotEmpty('close-button')) return null;

    // Find the element with the "close-button" slot attribute
    const slottedElement = this.querySelector(
      '[slot="close-button"]'
    ) as HTMLElement;

    // If the slotted element is already a button, return it
    if (slottedElement.tagName === 'BUTTON') {
      return slottedElement;
    }

    // If the slotted element is not a button, look for a nested button
    const nestedButton = slottedElement.querySelector('button');

    // Return the nested button if found, otherwise return null
    return nestedButton || null;
  }

  /**
   * Render the modal component
   */
  render(): TemplateResult {
    const modalClasses = {
      [`button-x-position-${this.closeButtonXPosition}`]: true,
      [`button-y-position-${this.closeButtonYPosition}`]: true,
      [`modal-size--${this.modalSize}`]: this.modalSize !== '',
    };

    return this.hasError
      ? html` <div class="error-message">${this.hasError}</div> `
      : html`
          <dialog
            class=${classMap(modalClasses)}
            @close=${this.closeModal}
            variant=${this.variant}
          >
            <div class="close-button-container">
              <slot name="close-button"></slot>
              ${this.slots.isNotEmpty('close-button-adjacent-content')
                ? html`<slot name="close-button-adjacent-content"></slot>`
                : nothing}
            </div>
            <div class="modal-content">
              <slot></slot>
            </div>
          </dialog>
        `;
  }
}

// Declare the custom element for TypeScript
declare global {
  interface HTMLElementTagNameMap {
    'outline-modal': OutlineModal;
  }
}
